import React, { useState, useContext, useEffect } from 'react';
import { 
  Container, Typography, Box, TextField, MenuItem, Button, 
  Grid, Paper, Alert, Snackbar, CircularProgress, FormControlLabel, Checkbox
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import Axios from 'axios';
import AxiosInstance from '../../AxiosInstance';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import StateContext from '../../Contexts/StateContext';
import { MasterCategories, CompanyCategories, DayLabourerCategories } from '../../Constants/BusinessOptions';
import LocationSelector from '../Common/LocationSelector';

const defaultIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});

function MapEvents({ setCoordinates }) {
    const map = useMap();
    useEffect(() => {
        map.on('click', (e) => {
            setCoordinates(e.latlng.lat, e.latlng.lng);
        });
    }, [map, setCoordinates]);
    return null;
}

function MapFlyTo({ location }) {
    const map = useMap();
    useEffect(() => {
        if (location[0] && location[1]) {
            map.flyTo(location, 13);
        }
    }, [location, map]);
    return null;
}

function RecommendBusiness() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const GlobalState = useContext(StateContext);
  const { config, environment } = require('../../index');

  // Determine business type based on URL
  const getBusinessType = () => {
    if (location.pathname.includes('ceg-ajanlasa')) return 'Company';
    if (location.pathname.includes('napszamos-ajanlasa')) return 'DayLabourer';
    return 'Master';
  };

  const businessType = getBusinessType();

  const [formData, setFormData] = useState({
    business_type: businessType,
    name: '',
    email: '',
    website: '',
    description: '',
    phone_number: '',
    whatsapp_number: '',
    category: '',
    is_recommended: true,
    is_urgent: false,
    brings_tools: false,
    szek: '',
    kozsegvaros: '',
    telepules: '',
    address: '',
    latitude: 45.8643,
    longitude: 25.7848,
    geocodingError: '',
    geocodingSuccess: false
  });

  // const [allSzekek, setAllSzekek] = useState([]);
  // const [allKozsegek, setAllKozsegek] = useState([]);
  // const [allTelepulesek, setAllTelepulesek] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGeocode = async () => {
    if (!formData.address) return;
    try {
      const response = await Axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(formData.address)}`);
      if (response.data && response.data.length > 0) {
        const { lat, lon } = response.data[0];
        setFormData(prev => ({
          ...prev,
          latitude: parseFloat(lat),
          longitude: parseFloat(lon),
          geocodingSuccess: true,
          geocodingError: ''
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          geocodingError: t('Thematic.GeocodingNoResult', 'Nem található koordináta a megadott címhez.'),
          geocodingSuccess: false
        }));
      }
    } catch (e) {
      setFormData(prev => ({
        ...prev,
        geocodingError: t('Thematic.GeocodingError', 'Hiba történt a keresés során.'),
        geocodingSuccess: false
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError('');

    try {
      const { geocodingError, geocodingSuccess, ...dataToSend } = formData;
      
      // Koordináták kezelése
      const lat = parseFloat(dataToSend.latitude);
      const lon = parseFloat(dataToSend.longitude);

      const payload = {
        ...dataToSend,
        // Biztosítsuk, hogy a helyszín adatok string formátumban vannak, ha esetleg ID-k lennének
        szek: String(dataToSend.szek || ''),
        kozsegvaros: String(dataToSend.kozsegvaros || ''),
        telepules: String(dataToSend.telepules || ''),
        // Koordináták kerekítése csak ha érvényes számok
        latitude: !isNaN(lat) ? lat.toFixed(6) : null,
        longitude: !isNaN(lon) ? lon.toFixed(6) : null,
        owner: GlobalState.userId // Bár ajánlásnál nem kötelező, de jó ha ott van
      };

      await AxiosInstance.post(config[environment].businessesEndpoint + 'create/', payload, {
        headers: {
          Authorization: `Bearer ${GlobalState.jwtAccessToken}`
        }
      });
      setSuccess(true);
      setTimeout(() => {
        if (businessType === 'Company') navigate(`/${i18n.language}/cegek`);
        else if (businessType === 'DayLabourer') navigate(`/${i18n.language}/napszamosok`);
        else navigate(`/${i18n.language}/mesterek`);
      }, 2000);
    } catch (err) {
      setError(t('Common.Error', 'Hiba történt a mentés során.'));
    } finally {
      setLoading(false);
    }
  };

  const getCategories = () => {
    let categories = [];
    if (businessType === 'Company') categories = [...CompanyCategories];
    else if (businessType === 'DayLabourer') categories = [...DayLabourerCategories];
    else categories = [...MasterCategories];

    return categories.sort((a, b) => {
      const labelA = (a.label.includes('.') ? t(a.label, a.label) : a.label).toLowerCase();
      const labelB = (b.label.includes('.') ? t(b.label, b.label) : b.label).toLowerCase();
      return labelA.localeCompare(labelB, i18n.language);
    });
  };

  const getTitle = () => {
    if (businessType === 'Company') return t('Business.CompanyRecommendationTitle', 'Cég ajánlása');
    if (businessType === 'DayLabourer') return t('Business.DayLabourerRecommendationTitle', 'Napszámos ajánlása');
    return t('Business.MasterRecommendationTitle');
  };

  const getSubtitle = () => {
    if (businessType === 'Company') return t('Business.CompanyRecommendationSubtitle', 'Ismer olyan céget, aki kiváló munkát végez? Ajánlja őt a közösségnek!');
    if (businessType === 'DayLabourer') return t('Business.DayLabourerRecommendationSubtitle', 'Ismer olyan megbízható napszámost, akit szívesen ajánlana másoknak is?');
    return t('Business.MasterRecommendationSubtitle');
  };

  const getNameLabel = () => {
    if (businessType === 'Company') return t('Business.CompanyName', 'Cég neve');
    if (businessType === 'DayLabourer') return t('Business.DayLabourerName', 'Napszámos neve');
    return t('Business.MasterName');
  };

  const getPhoneLabel = () => {
    if (businessType === 'Company') return t('Business.CompanyPhone', 'Cég telefonszáma');
    if (businessType === 'DayLabourer') return t('Business.DayLabourerPhone', 'Telefonszám');
    return t('Business.MasterPhone');
  };

  return (
    <Container maxWidth="md" sx={{ py: 4, mt: '50px' }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'var(--sznodarkblue)', fontWeight: 'bold' }}>
          {getTitle()}
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
          {getSubtitle()}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{t('Common.Success')}</Alert>}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={getNameLabel()}
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('Administration.Email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('Business.Website', 'Honlap')}
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://..."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={t('Common.Category')}
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
              >
                {getCategories().map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label.includes('.') ? t(cat.label, cat.label) : cat.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={getPhoneLabel()}
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('Business.WhatsAppNumber')}
                name="whatsapp_number"
                value={formData.whatsapp_number}
                onChange={handleChange}
                placeholder="+40..."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={formData.brings_tools} 
                    onChange={(e) => setFormData(prev => ({ ...prev, brings_tools: e.target.checked }))} 
                    name="brings_tools" 
                  />
                }
                label={t('Business.BringsTools', 'Hoz szerszámot')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={formData.is_urgent} 
                    onChange={(e) => setFormData(prev => ({ ...prev, is_urgent: e.target.checked }))} 
                    name="is_urgent" 
                  />
                }
                label={t('Business.UrgentWork', 'Vállal sürgős munkát')}
              />
            </Grid>
            <LocationSelector
              selectedSzek={formData.szek}
              selectedKozseg={formData.kozsegvaros}
              selectedTelepules={formData.telepules}
              onSzekChange={(val) => setFormData(prev => ({ ...prev, szek: val, kozsegvaros: '', telepules: '' }))}
              onKozsegChange={(val) => setFormData(prev => ({ ...prev, kozsegvaros: val, telepules: '' }))}
              onTelepulesChange={(val) => setFormData(prev => ({ ...prev, telepules: val }))}
            />
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label={t('Thematic.Address')}
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder={t('Thematic.AddressPlaceholder')}
                />
                <Button 
                  variant="outlined" 
                  onClick={handleGeocode}
                  sx={{ color: 'var(--sznodarkblue)', borderColor: 'var(--sznodarkblue)', minWidth: '120px' }}
                >
                  {t('Thematic.SearchOnMap')}
                </Button>
              </Box>
              {formData.geocodingError && <Typography variant="caption" color="error">{formData.geocodingError}</Typography>}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t('Thematic.ManualLocationTitle')}
              </Typography>
              <Box sx={{ height: 300, width: '100%', mb: 2, borderRadius: 1, overflow: 'hidden', border: '1px solid #ccc' }}>
                <MapContainer center={[formData.latitude, formData.longitude]} zoom={13} style={{ height: '100%', width: '100%' }}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <Marker position={[formData.latitude, formData.longitude]} icon={defaultIcon} />
                  <MapEvents setCoordinates={(lat, lng) => setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }))} />
                  <MapFlyTo location={[formData.latitude, formData.longitude]} />
                </MapContainer>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('Common.Description')}
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{ bgcolor: 'var(--sznodarkblue)', py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} /> : t('Business.SubmitRecommendation', 'Ajánlás beküldése')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
}

export default RecommendBusiness;
